import React from "react";
import { Typography } from "@material-ui/core";
import RequestTypeStep from "./requestTypeStep";
import ReviewRecordStep from "./reviewRecordStep";
import AssignUserStep from "./assignUserStep";
import DocumentSelectorStep from "./documentSelectorStep";
import RecordSelectorStep from "./recordSelectorStep";
import ReviewRequestStep from "./reviewRequestStep";
import {
  getAttribute,
  getAttributeValue,
  getEntityAddress,
} from "../../../../Utilities/Functions";

const getRecordName = (e, a) => {
  let name = getAttributeValue(getAttribute(e.attributes, "3"));
  if (a && a.associations && a.associations.length > 0) {
    const associationNames = a.associations
      .map((a) => a.associationTypeName)
      .sort();
    return (
      <span>
        <strong>{name}</strong>
        {" - "}
        <i>{Array.from(new Set(associationNames)).join(",")}</i>
      </span>
    );
  } else {
    return <strong> {name}</strong>;
  }
};

const getRequestTypeComponent = () => {
  return {
    getLabel: (t) =>
      t("labels.record.documentRequest.steps.requestTypeStep.stepLabel"),
    isDisabled: (request) => false,
    canSkip: (request) => false,
    getComponent: (props) => {
      return <RequestTypeStep {...props} />;
    },
    updateRequest: (request, params) => {
      return { ...request, requestType: params.requestType };
    },
  };
};

const getReviewRecordComponent = (entity, entityAssocaition) => {
  return {
    getLabel: (t) => (
      <Typography>
        {t("labels.record.documentRequest.steps.recordReviewStep.stepLabel", {
          recordType: entityAssocaition
            ? t(
                "labels.record.documentRequest.steps.recordReviewStep.associatedRecord",
              )
            : t("labels.record.documentRequest.steps.recordReviewStep.client"),
        })}
        {"("}
        {getRecordName(entity, entityAssocaition)}
        {")"}
      </Typography>
    ),
    isDisabled: (request) => !request.requestType,
    canSkip: (request) => false,
    getComponent: (props) => {
      return (
        <ReviewRecordStep
          {...props}
          entity={entity}
          entityAssocaition={entityAssocaition}
        />
      );
    },
    updateRequest: (request, params) => {
      const { clientType, entity, entityAssocaition, challengeResponse } =
        params;
      const { id, urn, attributes, addresses } = entity;
      const address = getEntityAddress(addresses);

      const records = request.records || [];
      const record = records.find((r) => r.entity.id === entity.id);
      if (!record) {
        if (entityAssocaition) {
          records.push({
            clientType,
            ...entityAssocaition,
          });
        } else {
          records.push({
            clientType,
            entity: { id, urn, attributes, address },
          });
        }
      } else {
        record.clientType = clientType;
      }

      return { ...request, records, challengeResponse };
    },
  };
};

const getClientUserComponent = () => {
  return {
    getLabel: (t) =>
      t("labels.record.documentRequest.steps.clientUserStep.stepLabel"),
    isDisabled: (request) => {
      return (
        !request.requestType ||
        !request.records ||
        request.records.filter((r) => !r.clientType).length > 0
      );
    },
    canSkip: (request) => false,
    getComponent: (props) => {
      return <AssignUserStep {...props} />;
    },
    updateRequest: (request, params) => {
      const { clientUser, userMessage } = params;
      return { ...request, clientUser, userMessage };
    },
  };
};

const getRecordSelectorComponent = () => {
  return {
    getLabel: (t) =>
      t("labels.record.documentRequest.steps.recordSelectionStep.stepLabel"),
    isDisabled: (request) => !request.requestType,
    canSkip: (request) => false,
    getComponent: (props) => {
      return <RecordSelectorStep {...props} />;
    },
    updateRequest: (request, params) => {
      const { selectedRecords } = params;
      const { records = [] } = request;
      const selectedIds = selectedRecords.map((r) => r.entity.id);
      const existingIds = records.map((r) => r.entity.id);
      const afterRemoving = records.filter(
        (r) => selectedIds.indexOf(r.entity.id) > -1,
      );
      for (let r of selectedRecords) {
        if (existingIds.indexOf(r.entity.id) < 0) {
          afterRemoving.push({
            ...r,
          });
        }
      }

      return {
        ...request,
        records: afterRemoving,
      };
    },
  };
};

const getDocumentSelectorComponent = (entity, entityAssocaition) => {
  return {
    getLabel: (t) => (
      <Typography>
        {t(
          "labels.record.documentRequest.steps.documentSelectionStep.stepLabel",
        )}
        {"("}
        {getRecordName(entity, entityAssocaition)}
        {")"}
      </Typography>
    ),
    isDisabled: (request) => !request.requestType,
    canSkip: (request) => false,
    getComponent: (props) => {
      return (
        <DocumentSelectorStep
          {...props}
          entity={entity}
          entityAssocaition={entityAssocaition}
        />
      );
    },
    updateRequest: (request, params) => {
      const {
        clientType,
        entity,
        entityAssocaition,
        documentTypes,
        clientUser,
        userMessage,
      } = params;
      const records = request.records || [];
      const record = records.find((r) => r.entity.id === entity.id);
      if (record) {
        record.clientType = clientType;
        record.documentTypes = documentTypes;
        record.clientUser = clientUser;
        record.userMessage = userMessage;
      } else if (entityAssocaition) {
        records.push({
          clientType,
          ...entityAssocaition,
          documentTypes,
          clientUser,
          userMessage,
        });
      } else {
        records.push({
          clientType,
          entity,
          documentTypes,
          clientUser,
          userMessage,
        });
      }

      return { ...request, records };
    },
  };
};

const getReviewRequestComponent = () => {
  return {
    getLabel: (t) =>
      t("labels.record.documentRequest.steps.reviewStep.stepLabel"),
    isDisabled: (request, steps, stepIndex, completed) => {
      if (
        !request.requestType ||
        !request.records ||
        request.records.filter((r) => !r.clientType).length > 0
      ) {
        return true;
      }
      for (let si = 0; si < steps.length; si++) {
        if (stepIndex !== si && !steps[si].canSkip(request) && !completed[si]) {
          return true;
        }
      }
      return false;
    },
    canSkip: (request) => false,
    getComponent: (props) => {
      return <ReviewRequestStep {...props} />;
    },
    updateRequest: (request, params) => {
      return request;
    },
  };
};

const getSteps = (request, entity, associatedEntities) => {
  const steps = [];
  steps.push(getRequestTypeComponent());
  if (request.requestType === "1") {
    steps.push(getReviewRecordComponent(entity));
    if (associatedEntities && associatedEntities.length > 0) {
      for (let ae of associatedEntities) {
        steps.push(getReviewRecordComponent(ae.entity, ae));
      }
    }
    steps.push(getClientUserComponent());
  }
  if (request.requestType === "2") {
    if (associatedEntities.length > 0) {
      steps.push(getRecordSelectorComponent());
      if (request.records && request.records.length > 0) {
        for (let r of request.records) {
          steps.push(getDocumentSelectorComponent(r.entity, r));
        }
      }
    } else {
      steps.push(getDocumentSelectorComponent(entity));
    }
  }
  if (request.requestType) {
    steps.push(getReviewRequestComponent());
  }

  return steps;
};

export default getSteps;
